import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { JsonObject } from "../api/client";
import { i18n } from "../i18n";

interface EnvironmentRow {
  key: string;
  value: string;
  visible: boolean;
}

@customElement("codex-env-editor")
export class CodexEnvEditor extends LitElement {
  @state() private rows: EnvironmentRow[] = [];
  @state() private error = "";

  static styles = css`
    :host {
      display: block;
      min-width: 0;
    }
    fieldset {
      min-width: 0;
      margin: 0;
      padding: 12px;
      border: 2px groove #fff;
    }
    legend {
      padding: 0 4px;
      font-weight: bold;
    }
    .rows {
      display: grid;
      gap: 10px;
      margin-bottom: 10px;
    }
    .row {
      display: grid;
      grid-template-columns: minmax(8rem, 0.8fr) minmax(10rem, 1.2fr) auto auto;
      align-items: end;
      gap: 8px;
      min-width: 0;
    }
    label {
      display: grid;
      min-width: 0;
      gap: 4px;
      font-weight: bold;
    }
    input {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      min-height: var(--text-field-height, 28px);
      padding: 3px 6px;
      border-top: 2px solid #000;
      border-left: 2px solid #000;
      border-right: 2px solid #fff;
      border-bottom: 2px solid #fff;
      background: #fff;
      font: inherit;
    }
    a2k-button {
      white-space: nowrap;
    }
    .error {
      margin: 0 0 10px;
      color: var(--danger, #a11919);
      font-weight: bold;
    }
    @media (max-width: 620px) {
      fieldset {
        padding: 10px 8px;
      }
      .row {
        grid-template-columns: minmax(0, 1fr) auto;
      }
      .key {
        grid-column: 1 / -1;
      }
      .value {
        grid-column: 1;
      }
      .visibility {
        grid-column: 2;
      }
      .remove {
        grid-column: 1 / -1;
        justify-self: start;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private readonly languageChanged = () => this.requestUpdate();

  private addRow = () => {
    this.rows = [...this.rows, { key: "", value: "", visible: false }];
    this.error = "";
  };

  private removeRow(index: number) {
    this.rows = this.rows.filter((_, rowIndex) => rowIndex !== index);
    this.error = "";
  }

  private updateRow(index: number, field: "key" | "value", value: string) {
    this.rows = this.rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    );
    this.error = "";
  }

  private toggleVisibility(index: number) {
    this.rows = this.rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, visible: !row.visible } : row,
    );
  }

  readValue(): JsonObject | null {
    const environment: JsonObject = {};
    const keys = new Set<string>();
    for (const row of this.rows) {
      const key = row.key.trim();
      if (!key && !row.value) continue;
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
        this.error = i18n.t("codexEnvironmentKeyInvalid");
        return null;
      }
      if (keys.has(key)) {
        this.error = i18n.t("codexEnvironmentDuplicate");
        return null;
      }
      keys.add(key);
      environment[key] = row.value;
    }
    this.error = "";
    return environment;
  }

  render() {
    return html`
      <fieldset>
        <legend>${i18n.t("codexEnvironment")}</legend>
        ${
          this.error
            ? html`<p class="error" role="alert">${this.error}</p>`
            : null
        }
        <div class="rows">
          ${this.rows.map(
            (row, index) => html`
              <div class="row" data-index=${index}>
                <label class="key">
                  <span>${i18n.t("codexEnvironmentKey")}</span>
                  <input
                    name="environment-key"
                    autocomplete="off"
                    .value=${row.key}
                    @input=${(event: Event) =>
                      this.updateRow(
                        index,
                        "key",
                        (event.target as HTMLInputElement).value,
                      )}
                  />
                </label>
                <label class="value">
                  <span>${i18n.t("codexEnvironmentValue")}</span>
                  <input
                    name="environment-value"
                    type=${row.visible ? "text" : "password"}
                    autocomplete="new-password"
                    .value=${row.value}
                    @input=${(event: Event) =>
                      this.updateRow(
                        index,
                        "value",
                        (event.target as HTMLInputElement).value,
                      )}
                  />
                </label>
                <a2k-button
                  class="visibility"
                  type="button"
                  aria-label=${
                    row.visible
                      ? i18n.t("codexHideEnvironmentValue")
                      : i18n.t("codexShowEnvironmentValue")
                  }
                  @click=${() => this.toggleVisibility(index)}
                >
                  ${
                    row.visible
                      ? i18n.t("codexHideEnvironmentValue")
                      : i18n.t("codexShowEnvironmentValue")
                  }
                </a2k-button>
                <a2k-button
                  class="remove"
                  type="button"
                  @click=${() => this.removeRow(index)}
                >
                  ${i18n.t("remove")}
                </a2k-button>
              </div>
            `,
          )}
        </div>
        <a2k-button type="button" @click=${this.addRow}>
          ${i18n.t("codexAddEnvironment")}
        </a2k-button>
      </fieldset>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "codex-env-editor": CodexEnvEditor;
  }
}
